import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import Banner from '@cpg-its/ads.containers.banner';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { TcsmEpiApiState, TcsmStates } from '../../../../root/tcsm-model';
import { ArcRoutes } from '../../constants/routes';
import { EmploymentState } from '../employment/employment-model';
import { useEmploymentInfo } from '../employment/useEmployment';
import { EmploymentTerminationForm } from './components/employment-termination-form';
import { getEmploymentTypeCombo } from './employment-termination-helpers';
import { personNeverWorkedHereRequest, terminateEmployeeRequest } from './employment-termination-requests';
import { useTerminationSettings } from './hooks/useTerminationSettings';
import { TerminationTypes } from './models/employment-termination-model';
import { EmploymentCombinationTypes, TerminationFormValues } from './models/termination-form-model';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { getFormattedDate, getNumberOfDays } from '../../../../global/helpers/date-helpers';
import { getEmployeesList } from '../../../../global/ducks/benadmin-api';
import { dispatchSetEmployeeList } from '../employment/employment-slice';
import { setEmployeeListLoaded } from '../institutions/institution-slice';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { WysiwygWithReactLink } from '../../../../global/components/wysiwyg-with-react-link';

interface Props {
	terminationSettingsId: string;
	isLoa?: boolean;
}

export function EmploymentTerminationApp(props: Props) {
	const { terminationSettingsId } = props;

	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const terminationProps = epiProps?.terminationSettingsState?.epiContent;
	const labelSettings = epiProps?.labelSettingState?.epiContent;
	const settingsLoaded = useTerminationSettings(terminationSettingsId);
	const employmentInfoLoaded = useEmploymentInfo();
	const employmentState = useSelector<TcsmStates, EmploymentState>(state => state.employmentState);
	const institutionName = useSelector<TcsmStates, string>(state => state.institutionState?.currentInstitution?.organization_name);
	const institutionClientNumber = useSelector<TcsmStates, string>(state => state.institutionState?.currentInstitution?.org_guid);
	const dispatch = useDispatch();
	const history = useHistory();
	const confirmationRoute = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.EmploymentTerminationConfirmation}`);
	const loaConfirmationRoute = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.UnpaidLeaveOfAbsence}`);


	const stipEmployment = employmentState?.currentEmployment?.employer_guid === institutionClientNumber
		? employmentState?.currentEmployment
		: null;

	const nonStipEmployment = employmentState?.nonStipendiaryEmployment?.employer_guid === institutionClientNumber
		? employmentState?.nonStipendiaryEmployment
		: null;

	const employmentTypeCombo = getEmploymentTypeCombo(stipEmployment, nonStipEmployment);

	useEffect(() => {
		dispatch(apiErrorClear());
	}, []);

	if (!settingsLoaded || !employmentInfoLoaded || !employmentTypeCombo) {
		return <LoadingCircle />;
	}

	if (employmentTypeCombo === EmploymentCombinationTypes.Terminated) {
		return <WysiwygWithReactLink content={epiProps?.employmentSettingsState?.epiContent?.noEmploymentMessage} />;
	}

	if (props.isLoa && nonStipEmployment && !stipEmployment) {
		return <WysiwygWithReactLink content={epiProps?.terminationSettingsState?.epiContent?.loaNotAvailableMessage} />;
	}

	const onSubmit = (values: TerminationFormValues) => {

		const today = new Date();
		const dayDiff = getNumberOfDays(values?.termination_date, today);
		const successRedirect = (messages: string[]) => {

			dispatch(getEmployeesList(institutionClientNumber, (response) => {
				dispatchSetEmployeeList(dispatch)(response);
				dispatch(setEmployeeListLoaded());

				history.push({
					pathname: values?.termination_type === TerminationTypes.LeaveOfAbsence ? loaConfirmationRoute : confirmationRoute,
					state: {
						message: messages,
						terminationType: values.termination_type,
						employmentTypes: values.employment_types,
						institution: institutionName,
						date: getFormattedDate(values?.termination_date || today?.toString()),
						immediate: dayDiff > -90 && dayDiff < 90 && !values?.extenuating_circumstances,
						hideSuccess: (dayDiff > 90 || dayDiff < -90 || values?.extenuating_circumstances) && values?.termination_type === TerminationTypes.Employment,
					},
				});
			}));
		};

		const failureCallback = () => {
			dispatch(getEmployeesList(institutionClientNumber, (response) => {
				dispatchSetEmployeeList(dispatch)(response);
				dispatch(setEmployeeListLoaded());
			}));
		};


		if (values.termination_type === TerminationTypes.Employment) {
			terminateEmployeeRequest(dispatch, employmentState?.currentEmployee?.guid, stipEmployment?.id, nonStipEmployment?.non_stipendiary_position_id, values, successRedirect, failureCallback);
			return;
		}

		const shallowCopy = { ...values };
		if (values?.termination_type === TerminationTypes.NeverWorkedHere) shallowCopy.termination_date = new Date().toString();

		personNeverWorkedHereRequest(dispatch, employmentState?.currentEmployee?.guid, stipEmployment?.id, nonStipEmployment?.non_stipendiary_position_id, shallowCopy, successRedirect);
	};

	return (
		<>
			{
				props?.isLoa &&
				<ContentSpacer>
					<Banner banner={
						{
							type: 'warning',
							label:
								<div className='rich-text' dangerouslySetInnerHTML={{ __html: terminationProps?.loaWarningMessage }} />,
						}
					}
					/>
				</ContentSpacer>
			}
			<PageHeader
				headerText={props?.isLoa ? terminationProps?.leaveOfAbsencePageTitle : terminationProps?.employmentTerminationHeader}
			/>

			<EmploymentTerminationForm
				terminationProps={terminationProps}
				labelSettings={labelSettings}
				employmentTypeCombo={employmentTypeCombo}
				stipEmployment={stipEmployment}
				nonStipEmployment={nonStipEmployment}
				onSubmit={onSubmit}
				isLoa={props?.isLoa}
			/>
		</>
	);
}

EmploymentTerminationApp.defaultProps = {
	isLoa: false,
};